import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Box, Typography } from '@mui/material'

import CustomButton from 'components/CustomButton'
import { CodingRoundScreens } from './constant'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  cardContainer: {
    display: 'flex',
    gap: theme.spacing(4),
    width: '100%'
  },
  cardRoot: {
    borderRadius: theme.spacing(1),
    boxShadow: `0px 0px 4px 0px #00000026`,
    backgroundColor: theme.palette.shades['clr-white-900'],
    padding: theme.spacing(10, 52.5, 6, 8),
    display: 'flex',
    width: '100%',
    minHeight: '320px',
    height: '100%',
    position: 'relative',
    [theme.breakpoints.down('1500')]: {
      minHeight: '400px'
    }
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  dsaImage: {
    position: 'absolute',
    right: '16px',
    top: '64px',
    width: '164px'
  },
  companyImage: {
    position: 'absolute',
    right: 0,
    top: 0,
    width: '206px'
  },
  bannerContainer: {
    position: "relative",
    overflow: "hidden",
    height: "150px",
  },
  commonStyle: {
    display: "flex",
    justifyContent: 'space-between',
    boxShadow: "0px 0px 4px 0px #00000026",
    backgroundColor: theme.palette.shades["clr-white-900"],
    borderRadius: theme.spacing(1),
    position: "absolute",
    width: "100%",
    height: "100%",
    transition: "opacity 0.5s ease-in-out",
  },
  betaContainer: {
    background: "linear-gradient(90deg, #FFFFFF 0%, #E8E4FF 100%)",
    padding: theme.spacing(8, 8, 0, 8),
  },
  comingSoonContainer: {
    background: "linear-gradient(90deg, #FFFFFF 0%, #FFF2DA 75.74%)",
    padding: theme.spacing(8),
  },
  hidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  visible: {
    opacity: 1,
    zIndex: 1,
  }
}))

const cardData = {
  DSA: {
    _id: 'DSA',
    heading: 'Prepare for DSA in Company Rounds in a Virtual Interview Scenario',
    content: [
      'Face real-time DSA mock interviews with latest problems',
      'Practice core topics: Arrays, Trees, Graphs, DP & more'
    ],
    buttonCta: 'Choose DSA',
    image: 'https://assets.languify.in/images/dsaBanner.png'
  },
  COMPANY: {
    _id: 'COMPANY',
    heading: 'Prepare with PYQs for Company-focused Coding Round in a Virtual Interview Scenario',
    content: [
      'Solve coding questions asked by Amazon, Google, and many more.',
      `Tailored interview flow as-per company's actual pattern`
    ],
    buttonCta: 'Choose Company',
    image: 'https://assets.languify.in/images/companyBannerIllustration.png'
  }
}

export const HeroCards = ({ setActiveScreen }) => {
  const classes = useStyles();
  return (
    <Box className={classes.cardContainer}>
      {Object.values(cardData).map((data, index) => (
        <Box className={classes.cardRoot} key={index}>
          <img
            src='https://assets.languify.in/images/gradientBlue.png'
            alt='texture'
            width={300}
            height='100%'
            style={{
              position: 'absolute',
              left: 0,
              top: 0
            }}
          />
          <Box display='flex' flexDirection='column' justifyContent='space-evenly'>
            <Box className={classes.cardContent}>
              <Typography variant='h5-semiBold'>{data.heading}</Typography>
              <ul style={{ paddingLeft: '1rem', margin: '8px 0' }}>
                {data.content.map((point, idx) => (
                  <li style={{ marginBottom: '8px' }} key={idx}>
                    <Typography variant='body01-bold' color='neutral.clr-700'>
                      {point}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
            <CustomButton
              onClick={
                () => setActiveScreen(
                  data._id === 'DSA' ? CodingRoundScreens.TOPICS : CodingRoundScreens.COMPANIES
                )
              }
              sx={{ width: 'fit-content' }}
              endIcon={
                <img
                  src='https://assets.languify.in/images/arrow-right-white.png'
                  alt='icon'
                  width={20}
                />
              }
            >
              <Box display='flex' gap='8px' alignItems='center'>
                {data.buttonCta}
              </Box>
            </CustomButton>
          </Box>
          <img
            src={data.image}
            alt='illustration'
            className={data._id === 'DSA' ? classes.dsaImage : classes.companyImage}
          />
        </Box>
      ))}
    </Box>
  )
}

const BetaBanner = () => {
  const classes = useStyles();
  const [showBeta, setShowBeta] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowBeta((prev) => !prev);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box className={classes.bannerContainer}>
      {/* Beta Container */}
      <Box
        className={`${classes.commonStyle} ${classes.betaContainer} ${showBeta ? classes.visible : classes.hidden
          }`}
      >
        <Box display="flex" flexDirection="column" gap="12px">
          <img src="https://assets.languify.in/images/betaTag.png" alt="tag" width={53} />
          <Typography variant="h6-medium" color="neutral.clr-800">
            Didn't find the company you want to prepare for? No worries, you can prepare for upcoming coding rounds based on job description & resume.
          </Typography>
        </Box>
        <img src="https://assets.languify.in/images/codingFlow.png" alt="flow" width={410} />
      </Box>

      {/* Coming Soon Container */}
      <Box
        className={`${classes.commonStyle} ${classes.comingSoonContainer} ${showBeta ? classes.hidden : classes.visible
          }`}
      >
        <Box display="flex" flexDirection="column" gap="12px">
          <img src="https://assets.languify.in/images/comingSoonTag.png" alt="tag" width={104} />
          <Typography variant="h6-medium" color="neutral.clr-800">
            Start your 30-day challenge to ace technical interviews - winners get a certificate and Letter of Recommendation.
          </Typography>
        </Box>
        <img src="https://assets.languify.in/images/30Days.png" alt="flow" width={165} />
      </Box>
    </Box>
  );
}

const HeroSection = ({ setActiveScreen }) => {
  const classes = useStyles();
  return (
    <Box className={classes.container}>
      <HeroCards setActiveScreen={setActiveScreen} />
      <BetaBanner />
    </Box>
  )
}

export default HeroSection